import type {ReactElement} from 'react';
import type {ComponentStreamEvent} from '@Components/ai-chat-window/ai-chat-window.types';
import {ChatMessagePartType} from '@Components/ai-chat-window/ai-chat-window.types';
import type {
  AnswersDraftProps,
  ChatMessageComponentPart,
  ParsedStreamComponentProps,
  RegisteredStreamComponent,
  StreamComponentContext,
  StreamComponentOptions,
  StreamComponentRegistration,
} from '@Components/ai-chat-window/lib/stream-component-registry.types';
import {ANSWERS_DRAFT_ID_PROP} from '@Components/ai-chat-window/lib/stream-component-registry.types';
import type {StreamComponentType} from '@Components/ai-chat-window/lib/stream-component-registry.types';
import {createRegistry} from '@Components/ai-chat-window/lib/create-registry';
import {isPlainObject} from '@Components/ai-chat-window/lib/stream-component-prop-guards';
import {planTableRegistration} from '@Components/ai-chat-window/components/plan-table/plan-table.registration';
import {buttonGroupRegistration} from '@Components/ai-chat-window/components/button-group/button-group.registration';
import {entityDraftCardRegistration} from '@Components/ai-chat-window/components/entity-draft-card/entity-draft-card.registration';
import {entityDraftSkeletonRegistration} from '@Components/ai-chat-window/components/entity-draft-skeleton/entity-draft-skeleton.registration';
import {entityCardRegistration} from '@Components/ai-chat-window/components/entity-card/entity-card.registration';
import {buttonGroupSkeletonRegistration} from '@Components/ai-chat-window/components/button-group-skeleton/button-group-skeleton.registration';
import {entityCardSkeletonRegistration} from '@Components/ai-chat-window/components/entity-card-skeleton/entity-card-skeleton.registration';
import {entityDraftGroupRegistration} from '@Components/ai-chat-window/components/entity-draft-group/entity-draft-group.registration';
import {entityCardGroupRegistration} from '@Components/ai-chat-window/components/entity-card-group/entity-card-group.registration';
import {optionGridRegistration} from '@Components/ai-chat-window/components/option-grid/option-grid.registration';
import {actionListRegistration} from '@Components/ai-chat-window/components/action-list/action-list.registration';
import {draftDiscardedNoteRegistration} from '@Components/ai-chat-window/components/draft-discarded-note/draft-discarded-note.registration';
import {webSourcesRegistration} from '@Components/ai-chat-window/components/web-sources/web-sources.registration';
import {webSourcesSkeletonRegistration} from '@Components/ai-chat-window/components/web-sources-skeleton/web-sources-skeleton.registration';

/**
 * Erase a typed registration's TProps for the registry: parseProps widens to unknown for
 * free, and render is wrapped to re-assert TProps at the boundary parseProps already
 * validated. Type-checked at registration time, so a mismatched parse/render pairing fails
 * to compile here rather than being silenced by an `as unknown as` at the collection site.
 */
function defineStreamComponent<TComponentType extends StreamComponentType, TProps>(
  registration: StreamComponentRegistration<TComponentType, TProps>,
  options: StreamComponentOptions = {parsesOnHydration: false}
): RegisteredStreamComponent {
  return {
    componentType: registration.componentType,
    parsesOnHydration: options.parsesOnHydration,
    parseProps: registration.parseProps,
    render: (props, context): ReactElement => registration.render(props as TProps, context),
  };
}

const REGISTRY = createRegistry(
  [
    defineStreamComponent(planTableRegistration),
    defineStreamComponent(buttonGroupRegistration),
    defineStreamComponent(entityDraftCardRegistration),
    defineStreamComponent(entityDraftSkeletonRegistration),
    defineStreamComponent(entityCardRegistration),
    defineStreamComponent(buttonGroupSkeletonRegistration),
    defineStreamComponent(entityCardSkeletonRegistration),
    defineStreamComponent(entityDraftGroupRegistration, {parsesOnHydration: true}),
    defineStreamComponent(entityCardGroupRegistration, {parsesOnHydration: true}),
    defineStreamComponent(optionGridRegistration, {parsesOnHydration: true}),
    defineStreamComponent(actionListRegistration, {parsesOnHydration: true}),
    defineStreamComponent(draftDiscardedNoteRegistration, {parsesOnHydration: true}),
    defineStreamComponent(webSourcesRegistration, {parsesOnHydration: true}),
    defineStreamComponent(webSourcesSkeletonRegistration),
  ],
  (registration) => registration.componentType
);

export const createComponentPart = (event: Pick<ComponentStreamEvent, 'componentType' | 'props'>): ChatMessageComponentPart | null => {
  const entry = REGISTRY[event.componentType];
  if (!entry) {
    return null;
  }

  const props = parsePartProps(entry, event.props);
  if (props === null) {
    return null;
  }

  // The entry's own parseProps validated these props for its component type, which the part union cannot see.
  return {
    type: ChatMessagePartType.COMPONENT,
    componentType: entry.componentType,
    props,
  } as unknown as ChatMessageComponentPart;
};

export const renderStreamComponent = (part: ChatMessageComponentPart, context: StreamComponentContext): ReactElement | null => {
  const entry = REGISTRY[part.componentType];
  if (!entry) {
    return null;
  }

  if (!entry.parsesOnHydration) {
    return entry.render(part.props, context);
  }

  const props = parsePartProps(entry, isPlainObject(part.props) ? part.props : {});
  if (props === null) {
    return null;
  }

  return entry.render(props, context);
};

const parsePartProps = (entry: RegisteredStreamComponent, props: Record<string, unknown>): ParsedStreamComponentProps | null => {
  const parsedProps = entry.parseProps(props);
  if (!isPlainObject(parsedProps)) {
    console.error(`Dropped malformed ${entry.componentType} stream component payload`);
    return null;
  }

  return {...parsedProps, ...answersDraftPropsOf(props)};
};

const answersDraftPropsOf = (props: Record<string, unknown>): AnswersDraftProps => {
  const answersDraftId = props[ANSWERS_DRAFT_ID_PROP];
  if (typeof answersDraftId === 'string') {
    return {[ANSWERS_DRAFT_ID_PROP]: answersDraftId};
  }

  return Array.isArray(answersDraftId) ? {[ANSWERS_DRAFT_ID_PROP]: answersDraftId.filter((draftId): draftId is string => typeof draftId === 'string')} : {};
};
